import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  closeResourcePanel,
  setIsEditing,
  setResourcePanelView,
  useUpdateTransportMutation,
  useBreadCrumbs,
  type EtlTransportResponse,
  type EtlApiTransportUpdateRequest,
  EtlApiTransportUpdateRequestSchema,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  getErrorStatus,
  getEtlSubmissionErrorMessage,
} from '@rsos/reduxDataStore/src/utils/errorUtils';
import {
  trackSubmitAirAmbulanceRequest,
  trackSubmitAirAmbulanceRequestApiResponse,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
import { getErrorMessage } from '../../AirAmbulanceForm/utils';
import { parseCoordinates } from '../../AirAmbulanceForm/validation';
import type { EditFormValues } from './useEditFormValues';

/**
 * Handles the submission of the Air Ambulance ETL Edit Form, including API interaction and tracking.
 * @param transport - The current transport data being edited
 * @returns An object containing the submit handler, cancel handler, and loading state
 */
export function useEditFormSubmit(transport: EtlTransportResponse): {
  handleSubmit: (values: EditFormValues) => Promise<void>;
  handleCancel: () => void;
  isLoading: boolean;
  error: string | null;
} {
  const dispatch = useDispatch();
  const [updateTransport] = useUpdateTransportMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const breadCrumbs = useBreadCrumbs();

  const handleSubmit = async (values: EditFormValues) => {
    if (isLoading) return;
    setIsLoading(true);
    // Clear any error from a previous submission attempt.
    setError(null);

    trackSubmitAirAmbulanceRequest({
      requestId: transport.gmr_transport_id,
      requestType: values.launch_type,
      uuid: transport.gmr_transport_id,
      isEdit: true,
    });

    try {
      const coords = parseCoordinates(values.pickup_coordinates);
      if (!coords || !coords.latitude || !coords.longitude) {
        throw new Error('Invalid coordinates');
      }
      const payload: EtlApiTransportUpdateRequest = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        landingZoneId: values.landing_zone_external_id || null,
        pickupLocationComments: values.pickup_details || undefined,
        multipleUnites: values.multiple_units_requested === 'Yes' || undefined,
        otherResponders: values.air_service_responding === 'Yes',
        otherRespondersDeclinedWeather: values.air_service_declined === 'Yes',
        groundContact: values.ground_contact || undefined,
        callerAddress: values.pickup_location || undefined,
      };
      const validPayload = EtlApiTransportUpdateRequestSchema.parse(payload);

      await updateTransport({
        transportId: transport.id,
        body: validPayload,
      }).unwrap();

      trackSubmitAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: 200,
        error: false,
        success: true,
        UUID: transport.gmr_transport_id,
        isEdit: true,
      });

      dispatch(setIsEditing(false));
    } catch (error) {
      const statusCode = getErrorStatus(error);

      trackSubmitAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: statusCode,
        error: getErrorMessage(error),
        success: false,
        UUID: transport.gmr_transport_id,
        isEdit: true,
      });

      // Surface a user-facing error so the form can render an error box.
      setError(getEtlSubmissionErrorMessage(error, statusCode));

      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Air Ambulance ETL Edit Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    dispatch(setIsEditing(false));
    if (breadCrumbs.includes('list_requests') && breadCrumbs.length > 1) {
      dispatch(setResourcePanelView('list_requests'));
    } else {
      dispatch(closeResourcePanel());
    }
  };

  return { handleSubmit, handleCancel, isLoading, error };
}
